// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { Button } from '@olxbr/ds-core'
import { useForm, Controller } from 'react-hook-form'
import PasswordInput from '../../molecules/PasswordInput'
import StrongPasswordInput, { isValidPassword } from '../../molecules/StrongPasswordInput'
import {
  BUTTON_TEXT,
  currentPasswordInputText,
  formContexts,
  isTheSamePassword,
  newPasswordInputText,
  passwordConfirmInputText,
} from './ChangePasswordForm.data'
import PropTypes from 'prop-types'
import ErrorHandler, { acceptedErrors } from './ErrorHandler'

const fieldErrors = [acceptedErrors.SAME_PASSWORD_ERROR, acceptedErrors.PASSWORD_MISMATCH_ERROR]

const ChangePasswordForm = ({
  isSubmitting,
  onSubmit,
  formContext = formContexts.ChangePassword,
  errorType,
}) => {
  const {
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    control,
    trigger,
  } = useForm({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    },
  })

  const [shouldShowSamePasswordError, setShouldShowSamePasswordError] = useState(false)
  const [shouldShowCurrentPasswordError, setShouldShowCurrentPasswordError] = useState(false)

  const canSubmit = !isSubmitting && isValid

  useEffect(() => {
    if (!isSubmitting) {
      if (errorType === acceptedErrors.SAME_PASSWORD_ERROR) {
        setShouldShowSamePasswordError(true)
        trigger('password')
      }

      if (errorType === acceptedErrors.PASSWORD_MISMATCH_ERROR) {
        setShouldShowCurrentPasswordError(true)
        trigger('currentPassword')
      }
    }
  }, [errorType, trigger, isSubmitting])

  return (
    <div className="flex flex-col">
      {errorType && !fieldErrors.includes(errorType) && <ErrorHandler errorType={errorType} />}
      {formContext === formContexts.ChangePassword && (
        <Controller
          name="currentPassword"
          control={control}
          rules={{
            required: true,
            validate: password => password.length > 0 && !shouldShowCurrentPasswordError,
            onChange: () => setShouldShowCurrentPasswordError(false),
          }}
          render={({ field }) => {
            return (
              <PasswordInput
                {...field}
                disabled={isSubmitting}
                label={currentPasswordInputText.label}
                arialabel={currentPasswordInputText.arialabel}
                errorMsg={
                  shouldShowCurrentPasswordError
                    ? currentPasswordInputText.errorMessage.passwordMismatch
                    : currentPasswordInputText.errorMessage.emptyPassword
                }
                hasError={!!errors.currentPassword || shouldShowCurrentPasswordError}
                marginBottom="--spacing-3"
              />
            )
          }}
        />
      )}
      <Controller
        name="password"
        control={control}
        rules={{
          required: true,
          validate: value => isValidPassword(value) && !shouldShowSamePasswordError,
          onChange: () => setShouldShowSamePasswordError(false),
          onBlur: () => {
            if (getValues('passwordConfirmation')) return trigger('passwordConfirmation')
          },
        }}
        render={({ field }) => (
          <StrongPasswordInput
            {...field}
            label={newPasswordInputText[formContext].label}
            marginBottom="--spacing-3"
            customErrorMsg={shouldShowSamePasswordError ? newPasswordInputText.errorMessage : ''}
            customHasError={shouldShowSamePasswordError}
          />
        )}
      />
      <Controller
        name="passwordConfirmation"
        control={control}
        rules={{
          required: true,
          validate: passwordConfirmation =>
            isTheSamePassword(getValues('password'), passwordConfirmation),
          onChange: () => trigger('passwordConfirmation'),
        }}
        render={({ field }) => (
          <PasswordInput
            {...field}
            disabled={isSubmitting}
            label={passwordConfirmInputText.label}
            arialabel={passwordConfirmInputText.arialabel}
            errorMsg={passwordConfirmInputText.errorMessage}
            hasError={!!errors.passwordConfirmation}
            marginBottom="--spacing-3"
          />
        )}
      />
      <Button
        className="w-full"
        size="medium"
        disabled={!canSubmit}
        onClick={handleSubmit(onSubmit)}
      >
        {BUTTON_TEXT}
      </Button>
    </div>
  )
}

ChangePasswordForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formContext: PropTypes.oneOf(Object.values(formContexts)),
  errorType: PropTypes.oneOf(Object.values(acceptedErrors)),
}

export { ChangePasswordForm }
